import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Button } from "../../shadcn/ui/button";
import { Badge } from "../../shadcn/ui/badge";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../shadcn/ui/dialog";
import { Checkbox } from "../../shadcn/ui/checkbox";
import { showApiMessage } from "../../utils/messageHandler";
import * as localService from "../../services/localService";
import apiV2Service from "../../services/apiV2Service";
import LoadingSpinner from "../../components/LoadingSpinner";
import logger from "../../utils/logger";


const RolesAndPermissions = () => {
  const [organizationRoles, setOrganizationRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const user = localService.getUser();
    setCurrentUser(user);
    if (user) {
      loadOrganizationRoles();
      loadAvailablePermissions();
    }
  }, []);

  // Set loading to false when both roles and permissions are loaded
  useEffect(() => {
    if (organizationRoles.length > 0 && availablePermissions.length > 0) {
      setLoading(false);
    }
  }, [organizationRoles, availablePermissions]);

  const loadOrganizationRoles = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiV2Service.getOrganizationRoles();
      setOrganizationRoles(response.data || []);
    } catch (error) {
      logger.error("Error loading organization roles:", error);
      setError("Failed to load organization roles. Please try again later.");
      showApiMessage(error, "load organization roles");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePermissions = async () => {
    setLoading(true);
    setError("");
    try {
      const permissions = await apiV2Service.getAllPermissions();
      setAvailablePermissions(permissions);
    } catch (error) {
      logger.error("Error loading permissions:", error);
      setError("Failed to load permissions. Please try again later.");
      showApiMessage(error, "load permissions");
    } finally {
      setLoading(false);
    }
  };

  const updateRolePermissions = async () => {
    if (!selectedRole) return;

    try {
      setUpdating(true);

      const payload = {
        name: selectedRole.name,
        permissions: selectedRole.permissions,
        active: selectedRole.active,
      };

      const updatedRole = await apiV2Service.updateOrganizationRole(selectedRole.id, payload);
      setOrganizationRoles(prevRoles => prevRoles.map(role => (role.id === updatedRole.id ? updatedRole : role)));
      const successResponse = {
        status: 200,
        data: { message: "Role permissions updated" },
      };
      showApiMessage(successResponse, "update role permissions");
      setEditDialogOpen(false);
      setSelectedRole(null);
    } catch (error) {
      logger.error("Error updating role permissions:", error);
      if (error.response) {
        logger.error("Error response:", error.response.data);
      }
      showApiMessage(error, "update role permissions");
    } finally {
      setUpdating(false);
    }
  };

  const handleEditRole = () => {
    if (!selectedRole.name) {
      const validationError = {
        response: {
          status: 400,
          data: { message: "Please fill in all required fields" },
        },
      };
      showApiMessage(validationError, "update role permissions");
      return;
    }

    updateRolePermissions();
  };

  const togglePermission = (permissionCode, rolePermissions) => {
    const updatedPermissions = rolePermissions.includes(permissionCode)
      ? rolePermissions.filter(p => p !== permissionCode)
      : [...rolePermissions, permissionCode];

    return updatedPermissions;
  };

  if (loading) {
    return (
      <div className="container p-6 mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Organization Roles & Permissions</h1>
          <p className="mt-2 text-gray-600">Manage permissions for existing organization roles</p>
        </div>
        <LoadingSpinner
          loading={true}
          message="Loading roles and permissions..."
          padding="py-16"
          className="text-blue-600"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  // Redirect non-admin users
  if (!currentUser || currentUser.organizationRole?.code !== "ROLE_ADMINISTRATOR") {
    window.location.href = "/dashboard";
    return null;
  }

  return (
    <div className="container p-6 mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Organization Roles & Permissions</h1>
          <p className="mt-2 text-gray-600">Manage permissions for existing organization roles</p>
        </div>
      </div>

      <div className="grid gap-6">
        {organizationRoles
          .filter(role => role !== null)
          .map(role => (
            <Card key={role.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex gap-2 items-center">
                      {role.name}
                      <Badge variant={role.active ? "default" : "secondary"}>
                        {role.active ? "Active" : "Inactive"}
                      </Badge>
                      {role.primary && (
                        <Badge variant="outline" className="text-xs">
                          Primary
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="mt-1 text-sm text-gray-600">{role.code}</p>
                    <p className="mt-1 text-sm text-gray-500">Organization Type ID: {role.organizationTypeId}</p>
                  </div>
                  <div className="flex gap-2">
                    <Dialog
                      open={editDialogOpen && selectedRole?.id === role.id}
                      onOpenChange={open => {
                        if (open) {
                          setSelectedRole(role);
                        } else {
                          setSelectedRole(null);
                        }
                        setEditDialogOpen(open);
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          Edit Permissions
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Edit Role Permissions</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="editRoleName">Role Name</Label>
                            <Input
                              id="editRoleName"
                              value={selectedRole?.name || ""}
                              disabled
                              className="bg-gray-100"
                              placeholder="Role name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="editRoleCode">Role Code</Label>
                            <Input
                              id="editRoleCode"
                              value={selectedRole?.code || ""}
                              disabled
                              className="bg-gray-100"
                              placeholder="e.g., ROLE_ADMIN"
                            />
                          </div>
                          <div>
                            <Label>Permissions</Label>
                            <div className="grid overflow-y-auto grid-cols-1 gap-2 max-h-60 md:grid-cols-2">
                              {availablePermissions.map(permission => (
                                <div key={permission.code} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={permission.code}
                                    checked={selectedRole?.permissions?.includes(permission.code) || false}
                                    onCheckedChange={checked => {
                                      if (checked) {
                                        setSelectedRole(prev => ({
                                          ...prev,
                                          permissions: [...(prev?.permissions || []), permission.code],
                                        }));
                                      } else {
                                        setSelectedRole(prev => ({
                                          ...prev,
                                          permissions: (prev?.permissions || []).filter(p => p !== permission.code),
                                        }));
                                      }
                                    }}
                                  />
                                  <Label htmlFor={permission.code} className="text-sm cursor-pointer">
                                    <div className="font-medium">{permission.name}</div>
                                    <div className="text-xs text-gray-500">{permission.code}</div>
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex justify-end pt-4 space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setEditDialogOpen(false);
                                setSelectedRole(null);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button onClick={handleEditRole} disabled={updating}>
                              {updating ? "Updating..." : "Update Permissions"}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div>
                  <h4 className="mb-3 font-medium">Permissions ({role.permissions?.length || 0})</h4>
                  <div className="flex flex-wrap gap-2">
                    {role.permissions && role.permissions.length > 0 ? (
                      role.permissions.map(permissionCode => {
                        const permission = availablePermissions.find(p => p.code === permissionCode);
                        return (
                          <Badge key={permissionCode} variant="outline" className="text-xs">
                            {permission ? permission.name : permissionCode}
                          </Badge>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500">No permissions assigned</p>
                    )}
                  </div>
                </div>
                <div className="mt-4 text-xs text-gray-500">
                  Created: {new Date(role.createdAt).toLocaleDateString()}
                  {role.modifiedAt && (
                    <span className="ml-4">Modified: {new Date(role.modifiedAt).toLocaleDateString()}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
      </div>

      <div className="my-8 border-t border-gray-200" />

      <div className="mt-8">
        <h2 className="mb-4 text-2xl font-bold text-gray-900">System Permissions Reference</h2>
        <p className="mb-6 text-gray-600">All available permissions that can be assigned to organization roles</p>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {availablePermissions.map(permission => (
            <Card key={permission.id}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{permission.name}</h3>
                    <p className="mt-1 text-sm text-gray-600">{permission.code}</p>
                    <p className="mt-1 text-sm text-gray-500">{permission.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RolesAndPermissions;
